/**
 * Glue between the shell (WP0-SHELL) and identity (WP0-AUTH), owned by the
 * integrator. Mounted once inside the signed-in shell:
 *
 * - Office selection: the shell's selector (URL `?office=`) is what the
 *   person sees. Every change is sent to identity's per-person context
 *   (`PUT /v1/identity/context`), so the office picked at sign-in and the
 *   one shown in the top bar never disagree, and identity's own requests
 *   carry the same `x-velora-office`.
 * - Live updates: a change to `membership`, `practice_role` or `location`
 *   can change the offices and permissions; both the identity session and
 *   the shell bootstrap are re-read.
 * - User menu: "My sign-in", "Lock screen" and "Sign out", also registered
 *   as actions (`identity.my-sign-in`, `identity.lock`, `identity.sign-out`)
 *   so the familiar menus that name them work (ADM-13: Open Dental's Log
 *   Off menu and File > Exit, Dentrix File > Exit).
 */
import type { LocationScope } from '@velora/contracts';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { scopeToParam } from '@velora/domain/tenancy';
import { useEffect, useRef } from 'react';
import {
  identityApi,
  identityKeys,
  setRequestScope,
  useSessionContext,
  type AuthState,
} from '../../modules/identity/index.js';
import { registerAction } from '../actions/registry.js';
import { registerUserMenuItem } from '../layout/TopBarParts.js';
import { useLive } from '../live/LiveProvider.js';
import { useOfficeScope } from '../office/OfficeScope.js';

/** Query key of the shell bootstrap (kept here to avoid a cycle with ShellRoot). */
export const SHELL_BOOTSTRAP_KEY = ['shell', 'bootstrap'] as const;

/** Entities whose changes can alter the signed-in person's offices or permissions. */
export const ACCESS_ENTITIES = ['membership', 'practice_role', 'location'] as const;

/** The identity selection as a URL/header param (`all` or an office id), or null. */
export function personScopeParam(state: AuthState): string | null {
  if (state.kind !== 'active') return null;
  const scope = state.session.context.scope;
  return scope ? scopeToParam(scope) : null;
}

/** Writes a new selection into the cached identity session without a refetch. */
export function adoptScope(qc: QueryClient, scope: LocationScope): void {
  qc.setQueryData<AuthState>(identityKeys.session, (old) =>
    old && old.kind === 'active'
      ? {
          ...old,
          session: { ...old.session, context: { ...old.session.context, scope } },
        }
      : old,
  );
}

function useScopeSync() {
  const qc = useQueryClient();
  const { state } = useSessionContext();
  const { scope, param, offices } = useOfficeScope();
  const held = personScopeParam(state);

  // Identity's own client sends the same office header as the shell.
  useEffect(() => {
    if (offices.length > 0) setRequestScope(scope);
  }, [scope, offices.length]);

  // Persist the shell's selection to identity after a short pause.
  const heldRef = useRef(held);
  heldRef.current = held;
  useEffect(() => {
    if (offices.length === 0 || heldRef.current === param) return undefined;
    const t = setTimeout(() => {
      identityApi
        .updateContext({ scope })
        .then(() => adoptScope(qc, scope))
        .catch(() => {
          // Not fatal: the shell preference and this device still remember it.
        });
    }, 600);
    return () => clearTimeout(t);
  }, [qc, scope, param, offices.length]);
}

function useAccessRefresh() {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: identityKeys.session });
    void qc.invalidateQueries({ queryKey: SHELL_BOOTSTRAP_KEY });
  };
  useLive(ACCESS_ENTITIES[0], refresh);
  useLive(ACCESS_ENTITIES[1], refresh);
  useLive(ACCESS_ENTITIES[2], refresh);
}

export interface IdentityVerbs {
  openMySignIn: () => void;
  lockNow: () => void;
  signOut: () => void;
}

/**
 * The identity verbs as actions (menus, toolbars, keymaps). Returns the
 * function that removes them.
 */
export function registerIdentityActions(verbs: () => IdentityVerbs): () => void {
  const offs = [
    registerAction({
      id: 'identity.my-sign-in',
      label: 'My sign-in and PIN',
      icon: 'shield-check',
      run: () => verbs().openMySignIn(),
    }),
    registerAction({
      id: 'identity.lock',
      label: 'Lock screen',
      icon: 'lock',
      run: () => verbs().lockNow(),
    }),
    registerAction({
      id: 'identity.sign-out',
      label: 'Sign out',
      run: () => verbs().signOut(),
    }),
  ];
  return () => {
    for (const off of offs) off();
  };
}

function useUserMenu() {
  const navigate = useNavigate();
  const { lockNow, signOut } = useSessionContext();
  const actions = useRef({ navigate, lockNow, signOut });
  actions.current = { navigate, lockNow, signOut };
  useEffect(() => {
    const offActions = registerIdentityActions(() => ({
      openMySignIn: () => void actions.current.navigate({ to: '/settings/my-sign-in' } as never),
      lockNow: () => void actions.current.lockNow(),
      signOut: () => void actions.current.signOut(),
    }));
    const off = [
      offActions,
      registerUserMenuItem({
        id: 'identity.my-sign-in',
        label: 'My sign-in and PIN',
        order: 800,
        onSelect: () => void actions.current.navigate({ to: '/settings/my-sign-in' } as never),
      }),
      registerUserMenuItem({
        id: 'identity.lock',
        label: 'Lock screen',
        order: 890,
        onSelect: () => void actions.current.lockNow(),
      }),
      registerUserMenuItem({
        id: 'identity.sign-out',
        label: 'Sign out',
        order: 900,
        tone: 'danger',
        onSelect: () => void actions.current.signOut(),
      }),
    ];
    return () => {
      for (const f of off) f();
    };
  }, []);
}

/** Renders nothing; keeps identity and the shell in step. */
export function IdentityBridge() {
  useScopeSync();
  useAccessRefresh();
  useUserMenu();
  return null;
}
